import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { HelpersService } from '../../helpers.service'
import { from, Observable } from 'rxjs'
import { PagesApiService } from 'src/app/builder-services/api/pages.api.service'

@Injectable({
  providedIn: 'root'
})
export class PagesService {
  allPages: any = []

  // Default pages configuration for website builder
  // Pages marked as undeletable cannot be removed
  defaultPages: any = [
    {
      page_name: 'home',
      title: 'Home',
      undeletable: true,
      template: 'websiteHomePage'
    },
    {
      page_name: 'shop',
      title: 'Shop',
      undeletable: false,
      template: 'websiteShopPage'
    },
    {
      page_name: 'product',
      title: 'Product',
      undeletable: false,
      template: 'websiteProductPage'
    },
    {
      page_name: 'contact',
      title: 'Contact',
      undeletable: false,
      template: 'websiteContactPage'
    },
    {
      page_name: 'cart',
      title: 'Cart',
      undeletable: false,
      template: 'websiteCartPage'
    },
    {
      page_name: 'checkout',
      title: 'Checkout',
      undeletable: false,
      template: 'websiteCheckoutPage'
    },
    {
      page_name: 'about',
      title: 'About',
      undeletable: false,
      template: 'websiteAboutPage'
    }
  ]

  options = {
    headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
  }

  private builderUrl

  constructor(
    private http: HttpClient,
    public helpers: HelpersService,
    private pagesApi: PagesApiService
  ) {
    this.builderUrl = this.helpers.getBuilderUrl()
  }

  /**
   * Get default page template by page name
   * @param pageName - Name of the page (home, shop, contact, etc.)
   * @param blockDataService - Reference to BlockDataService to access templates
   * @returns Block template array for the page
   */
  getPageTemplate = (pageName: string, blockDataService: any) => {
    const pageConfig = this.defaultPages.find((p: any) => p.page_name === pageName)
    if (pageConfig && pageConfig.template && blockDataService[pageConfig.template]) {
      return blockDataService[pageConfig.template]
    }
    return blockDataService.blankView || []
  }

  /**
   * Check if a page is undeletable
   * @param pageName - Name of the page
   * @returns Boolean indicating if page is undeletable
   */
  isPageUndeletable = (pageName: string): boolean => {
    const pageConfig = this.defaultPages.find((p: any) => p.page_name === pageName)
    return pageConfig?.undeletable || false
  }

  /**
   * Create all default pages for a project
   * @param project_id - The project ID
   * @param blockDataService - Reference to BlockDataService to access templates
   * @returns Observable with the response
   */
  createDefaultPages = (project_id: any, blockDataService: any) => {
    return new Observable<any>((observer) => {
      const pagesData: any = {}

      this.defaultPages.forEach((pageConfig: any) => {
        const page_id = Date.now().toString() + '-' + pageConfig.page_name
        const template = this.getPageTemplate(pageConfig.page_name, blockDataService)

        // Filter out header and footer blocks - they are stored in statics
        const contentBlocks = template.filter((block: any) =>
          block.type !== 'header' && block.type !== 'footer'
        )

        pagesData[page_id] = {
          page_id: page_id,
          page_name: pageConfig.page_name,
          title: pageConfig.title,
          undeletable: pageConfig.undeletable || false,
          all_blocks: JSON.stringify(contentBlocks)
        }
      })

      this.submitWebsitePages(project_id, pagesData).subscribe(
        (response) => {
          observer.next(response)
          observer.complete()
        },
        (error) => {
          observer.error(error)
        }
      )
    })
  }

  /**
   * Submit/Update a page configuration to Firebase
   * @param page_name - Name of the page
   * @param project_id - Project ID
   * @param page_id - Page ID (optional for new pages)
   * @param all_blocks - Block configuration JSON
   * @param undeletable - Whether the page is undeletable (defaults to false)
   * @returns Observable with the response
   */
  submitPage = (page_name: string, project_id: any, page_id: any = '', all_blocks: any = '', undeletable: boolean = false) => {
    let data = {
      all_blocks: all_blocks,
      page_id: page_id,
      page_name: page_name,
      title: page_name,
      undeletable: undeletable
    }
    return new Observable<any>((observer) => {
      this.pagesApi.upsertProjectPage(project_id, data)
        .then(() => {
          observer.next(data)
          observer.complete()
        })
        .catch((error) => observer.error(error))
    })
  }

  /**
   * Submit page data directly to Firebase Realtime Database
   * This method stores the entire pages object under projects/{project_id}/pages/
   * @param project_id - The project ID
   * @param data - The pages data object
   * @returns Observable with the response
   */
  submitWebsitePages = (project_id: any, data: any) => {
    return new Observable<any>((observer) => {
      Promise.all(
        Object.values(data as Record<string, any>).map((page: any) =>
          this.pagesApi.upsertProjectPage(project_id, page)
        )
      )
        .then(() => {
          observer.next(data)
          observer.complete()
        })
        .catch((error) => observer.error(error))
    })
  }

  /**
   * Get all pages for a specific project
   * @param project_id - The project ID
   * @returns Observable with all pages data
   */
  /** Renames a page. */
  updatePageName = (project_id: any, pageId: string, pageName: string): Promise<void> => {
    return this.pagesApi.updateProjectPageName(String(project_id), pageId, pageName)
  }

  /** Header or footer static block, defaulting to {} when unset. */
  getStaticBlock = (project_id: any, blockType: 'header' | 'footer'): Observable<any> => {
    return from(this.pagesApi.getProjectPageStatic(String(project_id), blockType).then(data => data ?? {}))
  }

  /** Saves the header or footer static block. */
  setStaticBlock = (project_id: any, blockType: 'header' | 'footer', data: any): Observable<void> => {
    return from(this.pagesApi.upsertProjectPageStatic(String(project_id), blockType, data))
  }

  /** Upserts a page row. */
  upsertPage = (project_id: any, page: any): Promise<void> => {
    return this.pagesApi.upsertProjectPage(String(project_id), page)
  }

  /** The project's raw pages, keyed by page id. */
  getProjectPages = (project_id: any): Promise<Record<string, any>> => {
    return this.pagesApi.getProjectPages(String(project_id))
  }

  /** The header or footer static block for the project. */
  getProjectPageStatic = (project_id: any, type: 'header' | 'footer'): Promise<any | null> => {
    return this.pagesApi.getProjectPageStatic(String(project_id), type)
  }

  getPages = (project_id: any) => {
    return new Observable<any>((observer) => {
      // this.db.getDatabase('projects/' + project_id + '/pages/').subscribe(...)  // Firebase
      from(this.pagesApi.getProjectPages(project_id)).subscribe(
        (response) => {
          observer.next(response)
          observer.complete()
        },
        (error) => {
          observer.error(error)
        }
      )
    })
  }

  /**
   * Get a specific page by ID
   * @param project_id - The project ID
   * @param page_id - The page ID
   * @returns Observable with the page data
   */
  getPageById = (project_id: any, page_id: any) => {
    return new Observable<any>((observer) => {
      // this.db.getDatabase('projects/' + project_id + '/pages/' + page_id).subscribe(...)  // Firebase
      from(this.pagesApi.getProjectPage(project_id, page_id)).subscribe(
        (response) => {
          observer.next(response)
          observer.complete()
        },
        (error) => {
          observer.error(error)
        }
      )
    })
  }

  /**
   * Delete a page from Firebase
   * @param project_id - The project ID
   * @param page_id - The page ID to delete
   * @returns Observable with the response
   */
  deletePage = (project_id: any, page_id: any) => {
    return new Observable<any>((observer) => {
      this.pagesApi.deleteProjectPage(project_id, page_id)
        .then(() => {
          observer.next(null)
          observer.complete()
        })
        .catch((error) => observer.error(error))
    })
  }

  /**
   * Format pages data from Firebase for UI display
   * @param allData - Raw pages data from Firebase
   * @param project_id - The project ID
   * @returns Formatted pages array
   */
  allPagesFormatter = (allData: any, project_id: any) => {
    this.allPages = []
    for (const key in allData) {
      if (Object.prototype.hasOwnProperty.call(allData, key)) {
        const element = allData[key]
        let element_data = element.all_blocks || element.allBlocks || element.data || ''

        let pageData = {
          projectId: project_id,
          allBlocks: typeof element_data === 'string' ? element_data : JSON.stringify(element_data),
          page_id: element.page_id || key,
          page_name: element.page_name,
          title: element.title || element.page_name,
          undeletable: element.undeletable || false
        }
        this.allPages.push(pageData)
      }
    }
    return this.allPages
  }
}
